import { useState } from 'react'
import { CheckCircle, XCircle, Clock, Loader2, PackageCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useDeploymentApprovals, useApproveDeployment, useRejectDeployment } from '@/hooks/useApi'

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function expiresIn(dateStr: string): string {
  const diff = new Date(dateStr).getTime() - Date.now()
  if (diff < 0) return 'expired'
  const hrs = Math.floor(diff / 3_600_000)
  const mins = Math.floor((diff % 3_600_000) / 60_000)
  if (hrs > 0) return `expires in ${hrs}h${mins > 0 ? ` ${mins}m` : ''}`
  return `expires in ${mins}m`
}

export function DeploymentApprovalsPanel() {
  const { data, isLoading, error } = useDeploymentApprovals()
  const approveMutation = useApproveDeployment()
  const rejectMutation = useRejectDeployment()
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const handleApprove = (approvalId: string) => {
    approveMutation.mutate({ approvalId })
  }

  const handleReject = async (approvalId: string) => {
    await rejectMutation.mutateAsync({ approvalId, reason: rejectReason || undefined }).catch(() => null)
    setRejectingId(null)
    setRejectReason('')
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
        {error instanceof Error ? error.message : 'Failed to load approvals'}
      </div>
    )
  }

  const approvals = data?.approvals ?? []

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Deploy Approvals</h2>
        <p className="text-sm text-text-muted mt-0.5">
          {approvals.length} pending review
        </p>
      </div>

      {approvals.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <PackageCheck className="h-10 w-10 mx-auto mb-3 opacity-20" />
          <p className="text-sm font-medium">No pending deployments</p>
          <p className="text-xs mt-1 opacity-60">Deployment requests needing approval appear here</p>
        </div>
      ) : (
        <div className="space-y-3">
          {approvals.map((approval) => (
            <Card key={approval.approval_id} className="p-4 bg-surface/30 border-border">
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-text-primary text-sm">{approval.worker_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        approval.environment === 'production'
                          ? 'bg-danger/10 text-danger'
                          : approval.environment === 'staging'
                          ? 'bg-warning/10 text-warning'
                          : 'bg-accent/10 text-accent'
                      }`}>
                        {approval.environment}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-xs text-text-muted flex-wrap">
                      <span>by {approval.requester_name || approval.requester_email}</span>
                      <span>·</span>
                      <Clock className="h-3 w-3" />
                      <span>{timeAgo(approval.created_at)}</span>
                      <span>·</span>
                      <span className="text-warning/80">{expiresIn(approval.expires_at)}</span>
                    </div>
                  </div>
                </div>

                {rejectingId === approval.approval_id ? (
                  <div className="space-y-2">
                    <textarea
                      placeholder="Reason for rejection (optional)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full rounded-md border border-border bg-bg text-text-primary px-3 py-2 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger hover:bg-danger/10"
                        onClick={() => handleReject(approval.approval_id)}
                        disabled={rejectMutation.isPending}
                      >
                        {rejectMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                        Confirm reject
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setRejectingId(null); setRejectReason('') }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-success hover:bg-success/10"
                      onClick={() => handleApprove(approval.approval_id)}
                      disabled={approveMutation.isPending}
                    >
                      {approveMutation.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                        : <CheckCircle className="h-3.5 w-3.5 mr-1.5" />}
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:bg-danger/10"
                      onClick={() => { setRejectingId(approval.approval_id); setRejectReason('') }}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1.5" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
